import React, { useRef, useState, useEffect } from "react";
import { fireGoogleTags } from "../../helpers/Helper";
import { useAppContext } from "@components/Provider";
import Sites from "../../../src/constants/Sites";

import styles from "./DealsPageAffiliateButton.module.css";

const DealsPageAffiliateButton = (props) => {
  const {
    amazonLink,
    itunesLink,
    bnLink,
    googleLink,
    koboLink,
    bamLink,
    bookShopLink,
    title,
    primary_isbn,
    isbns,
    publisher,
    preferredRetailer,
    retailerFromURL,
    className = "",
    buttonText = "BUY",
  } = props;

  const retailerSelectRef = useRef("");
  const firstUpdate = useRef(true);
  const [retailerLink, setRetailerLink] = useState("");
  const [selectedRetailerDisplayName, setSelectedRetailerDisplayName] = useState("");
  const [selectedRetailerUrl, setSelectedRetailerUrl] = useState("");
  let { siteId } = useAppContext();
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    fireGoogleTags();
  }, [retailerLink]);

  const shouldShowBookshop = process.env.SHOW_BOOKSHOP === "true";

  let retailers = [
    {
      display: "Amazon",
      kmName: "Amazon",
      url: amazonLink || "",
    },
    {
      display: "Apple Books",
      kmName: "iTunes",
      url: itunesLink,
    },
    {
      display: "Barnes & Noble",
      kmName: "Barnes&Noble",
      url: bnLink,
    },
    {
      display: "Google",
      kmName: "Google",
      url: googleLink,
    },
    {
      display: "Kobo",
      kmName: "Kobo",
      url: koboLink,
    },
  ];

  // Only add Bookshop if enabled
  if (shouldShowBookshop) {
    retailers.push({
      display: "Bookshop.org",
      kmName: "bookshop",
      url: bookShopLink,
    });
  }

  useEffect(() => {
    const getRetailerName = (name) => {
      if (!name) return null;
      switch (name) {
        case "BarnesNoble":
          return "Barnes & Noble";
        case "Apple":
          return "Apple Books";
        case "bookshop":
        case "Bookshop.org":
          return "Bookshop.org";
        default:
          return name;
      }
    };

    const selectedRetailerName = getRetailerName(preferredRetailer);

    // Try to get preferred retailer
    let selectedRetailer = retailers.find((r) => r.display === selectedRetailerName);

    // Fallback to first valid retailer
    if (!selectedRetailer) {
      selectedRetailer = retailers[0];
    }

    if (selectedRetailer) {
      const displayName = selectedRetailer.display;
      const url = selectedRetailer.url;
      setSelectedRetailerDisplayName(displayName);
      setSelectedRetailerUrl(url);
      setRetailerLink(url);
    }

    firstUpdate.current = false;
  }, [preferredRetailer]);

  if (!className.includes("searchAff")) {
    retailers.push({
      display: "Books A Million",
      kmName: "Books A Million",
      url: bamLink,
    });
  }

  if (retailerFromURL) {
    retailers = retailers.filter((el) => el.display === retailerFromURL);
  }

  const retailClick = (gaName) => {
    let gaPayload = {
      eventCategory: "Ebook",
      eventLabel: `${title} | ${primary_isbn || isbns} ${publisher ? "|" + " " + publisher : ""}`,
      eventAction: `Retail Click, ${gaName}`,
    };

    if (typeof window != "undefined" && window.dataLayer != undefined) {
      if (primary_isbn || (isbns && isbns != [])) {
        //ga("orim180.send", "event", gaPayload);
        window.dataLayer.push({ event: gaPayload.eventAction, ...gaPayload });
      }
    }
  };

  const handleSubmit = (e) => {
    if (retailerSelectRef.current.value) {
      const { url, displayName } = JSON.parse(retailerSelectRef.current.value);
      setSelectedRetailerUrl(url);
      if (url) {
        retailClick(displayName);
        setRetailerLink(url);
        setSelectedRetailerDisplayName(displayName);
      } else {
        e.preventDefault();
      }
    }
  };

  const handleRetailerChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === "button-placeholder") return;
    try {
      const { url, displayName } = JSON.parse(selectedValue);
      setSelectedRetailerDisplayName(displayName);
      setSelectedRetailerUrl(url);
    } catch (error) {
      console.error("Failed to parse retailer option", error);
    }
  };

  const renderButton = () => {
    let retailersList = [];
    retailers.forEach((retailer) => {
      retailersList.push(
        <option
          value={JSON.stringify({
            url: retailer.url,
            displayName: retailer.display,
          })}
          disabled={!retailer.url}
          key={`button-${retailer.display}`}
        >
          {!retailer.url ? `${retailer.display} (Not Available)` : retailer.display}
        </option>
      );
    });
    retailersList.unshift(
      <option value="button-placeholder" disabled key="button-placeholder">
        {"Select your Retailer"}
      </option>
    );

    const selectValue = selectedRetailerDisplayName
      ? JSON.stringify({
          url: selectedRetailerUrl,
          displayName: selectedRetailerDisplayName,
        })
      : "button-placeholder";
    const chevron =
      "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAA3klEQVRIS+3VMU9CMRTF8d8zBL+aizoQFhx0kUk33RzdYMNFXUFnYeGrYYyaJiUxJHDLSxodbNKpfeffc9/pbaPyaCrr+3OA++z4rtT5Pg5GuMnCY9yWQEoBE1xhlUUP8YDrCBIB0vojLvGO0yz4hm4JJAKcYYoPHGOZAUdYoIMBXrc5iQAHeMlzviFygj7O8dkWEJU4XI8chALRhn9AVKHf70VRTHu4wFfbmKZLNKt50dLBnna0imcMd/2I0phWa3Y/D1e1Xa9BCZJG0VuQNpaWKMx72xS1Fl5/WN3BN+AgJhnZQlq4AAAAAElFTkSuQmCC";
    return (
      <div
        className={[
          styles["dealspage__book_retailers"],
          "dealspage__book_retailers",
          ...className
            .split(" ")
            .map((classItem) => styles[classItem])
            .filter(Boolean),
        ].join(" ")}
        style={{ "--chevronURL": `url(${chevron})` }}
      >
        <div className={[styles["dealspage__dropdown"], "dealspage__dropdown"].join(" ")}>
          <select
            className={[styles["aff__link"], styles["select"]].join(" ")}
            ref={retailerSelectRef}
            onChange={handleRetailerChange}
            value={selectValue}
          >
            {retailersList}
          </select>
        </div>
        <div
          className={[
            styles["dealspage__buy_button"],
            "dealspage__buy_button",
            !selectedRetailerUrl && styles["dealspage__buy_button_disabled"],
            siteId === Sites.OPENROADMEDIA && selectedRetailerUrl
              ? styles["dealspage__buy_button_ORM"]
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <a
            href={selectedRetailerUrl ? retailerLink : "#"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleSubmit}
            style={{
              pointerEvents: selectedRetailerUrl ? "auto" : "none",
              opacity: selectedRetailerUrl ? 1 : 0.5,
            }}
          >
            {buttonText}
          </a>
        </div>
      </div>
    );
  };
  return <React.Fragment>{renderButton()}</React.Fragment>;
};

export default DealsPageAffiliateButton;
